import type { FamilyHistoryState } from "./FamilyHistoryContext";

export function transformFamilyHistoryStateToRequest(
  state: FamilyHistoryState,
): Record<string, unknown> {
  const familyHistory: Record<string, unknown> = {};

  // ancestryHistory → delete old entries then create new ones to avoid duplicates
  {
    const deleteOps = state._originalAncestryHistoryIds.map((id) => ({
      id,
      op: "delete",
    }));
    const createOps = state.ancestryHistory.map((a) => ({ ancestry: a }));
    const ancestryPayload = [...deleteOps, ...createOps];
    if (ancestryPayload.length > 0) {
      familyHistory.ancestryHistory = ancestryPayload;
    }
  }

  if (state.hasDoneDNAAncestryReport !== null) {
    familyHistory.hasDoneDNAAncestryReport = state.hasDoneDNAAncestryReport;
  }

  if (state.hasDoneDNAAncestryReport === true) {
    familyHistory.dnaAncestryDocumentId = state.dnaAncestryDocumentId;
    familyHistory.dnaAncestryReportDate = state.dnaAncestryReportDate;
  } else if (state.hasDoneDNAAncestryReport === false) {
    familyHistory.dnaAncestryDocumentId = null;
    familyHistory.dnaAncestryReportDate = null;
  }

  if (state.adoptionStatus !== null) {
    familyHistory.adoptionStatus = state.adoptionStatus;
  }

  const familyMemberDeleteOps = state._originalFamilyMemberIds.map((id) => ({
    id,
    op: "delete",
  }));
  const skipsBiologicalFamilyQuestions =
    state.adoptionStatus === "adopted_or_dont_know_biological_parents" ||
    state.adoptionStatus === "from_gamete_donation";

  // Only include family-specific fields if not adopted
  if (state.adoptionStatus === "not_adopted") {
    if (state.hasRelativeFacingFertilityIssues !== null) {
      familyHistory.hasRelativeFacingFertilityIssues =
        state.hasRelativeFacingFertilityIssues;
    }

    // Family member rows are inapplicable for both No and Unknown. Emit real
    // server-ID deletes in those branches; anonymous failed rows are handled
    // separately by the exact-edit recovery policy.
    if (state.hasRelativeFacingFertilityIssues === "yes") {
      const createOps = state.familyMemberWithFertilityIssues.map((m) => ({
        familyMember: m,
      }));
      const memberPayload = [...familyMemberDeleteOps, ...createOps];
      if (memberPayload.length > 0) {
        familyHistory.familyMemberWithFertilityIssues = memberPayload;
      }
    } else if (
      state.hasRelativeFacingFertilityIssues === "no" ||
      state.hasRelativeFacingFertilityIssues === "unknown"
    ) {
      if (familyMemberDeleteOps.length > 0) {
        familyHistory.familyMemberWithFertilityIssues = familyMemberDeleteOps;
      }
    }

    if (state.hasMotherTakenDESDuringPregnancy !== null) {
      familyHistory.hasMotherTakenDESDuringPregnancy =
        state.hasMotherTakenDESDuringPregnancy;
    }

    if (state.diseasesMothersSide !== null) {
      familyHistory.diseasesMothersSide = state.diseasesMothersSide;
    }
    if (state.diseasesFathersSide !== null) {
      familyHistory.diseasesFathersSide = state.diseasesFathersSide;
    }
  } else if (
    skipsBiologicalFamilyQuestions &&
    familyMemberDeleteOps.length > 0
  ) {
    // Both deployed adopted/non-biological branches skip every biological
    // family question, so known family-member rows must be removed on wire.
    familyHistory.familyMemberWithFertilityIssues = familyMemberDeleteOps;
  }
  if (skipsBiologicalFamilyQuestions) {
    // Clear inapplicable scalar answers explicitly. This keeps a pending
    // correction authoritative across server refetch/reapply and prevents
    // stale biological-family answers from remaining persisted but hidden.
    familyHistory.hasRelativeFacingFertilityIssues = null;
    familyHistory.hasMotherTakenDESDuringPregnancy = null;
    familyHistory.diseasesMothersSide = null;
    familyHistory.diseasesFathersSide = null;
  }

  if (Object.keys(familyHistory).length === 0) {
    return {};
  }
  return { familyHistory };
}
